import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface ApiAuthSecret {
  id: string;
  label: string;
  configured?: boolean;
}

export interface ApiAuthPlacement {
  secretId: string;
  in: 'query' | 'header' | 'body';
  name: string;
  prefix?: string;
}

export interface ApiOperation {
  id: string;
  method?: string;
  path?: string;
  description?: string;
}

export interface ApiIntegration {
  apiId: string;
  name: string;
  description: string | null;
  templateKey: string;
  baseUrl: string;
  host?: string;
  auth: {
    secrets: ApiAuthSecret[];
    placements: ApiAuthPlacement[];
  };
  operations: ApiOperation[];
  credentialSource: 'platform' | 'org';
  status: string;
  lastVerifiedAt: string | null;
  orgId: string;
  orgName?: string | null;
  agentIds: string[];
}

export interface VerifyApiResult {
  ok: boolean;
  apiId?: string;
  operation?: string;
  status?: number;
  message?: string;
  lastVerifiedAt?: string | null;
}

export function apiHost(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] || url;
  }
}

export const apiIntegrationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getApiIntegrations: builder.query<ApiIntegration[], { orgId: string } | void>({
      query: () => '/portal/api-integrations',
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ apiIntegrations: ApiIntegration[] }>(
          response,
        );
        return normalized.apiIntegrations ?? [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((row) => ({
                type: 'ApiIntegration' as const,
                id: row.apiId,
              })),
              { type: 'ApiIntegration', id: 'LIST' },
            ]
          : [{ type: 'ApiIntegration', id: 'LIST' }],
    }),

    getApiIntegration: builder.query<ApiIntegration, string>({
      query: (id) => `/portal/api-integrations/${id}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiIntegration>(response),
      providesTags: (_r, _e, id) => [{ type: 'ApiIntegration', id }],
    }),

    createApiIntegration: builder.mutation<
      ApiIntegration,
      { template_key: string }
    >({
      query: (body) => ({
        url: '/portal/api-integrations',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiIntegration>(response),
      invalidatesTags: [{ type: 'ApiIntegration', id: 'LIST' }],
    }),

    updateApiIntegration: builder.mutation<
      ApiIntegration,
      {
        id: string;
        body: {
          auth?: {
            secrets: { id: string; label: string }[];
            placements: {
              secret_id: string;
              in: string;
              name: string;
              prefix?: string;
            }[];
          };
          credentials?: Record<string, string>;
          use_platform_key?: boolean;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/portal/api-integrations/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiIntegration>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ApiIntegration', id },
        { type: 'ApiIntegration', id: 'LIST' },
      ],
    }),

    deleteApiIntegration: builder.mutation<
      { deleted: boolean },
      { id: string; confirm?: boolean }
    >({
      query: ({ id, confirm = true }) => ({
        url: `/portal/api-integrations/${id}`,
        method: 'DELETE',
        body: { confirm },
      }),
      invalidatesTags: [{ type: 'ApiIntegration', id: 'LIST' }, 'Agent'],
    }),

    verifyApiIntegration: builder.mutation<VerifyApiResult, { id: string }>({
      query: ({ id }) => ({
        url: `/portal/api-integrations/${id}/verify`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<VerifyApiResult>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ApiIntegration', id },
        { type: 'ApiIntegration', id: 'LIST' },
      ],
    }),

    updateApiAgentAssociations: builder.mutation<
      ApiIntegration,
      { id: string; agentIds: string[] }
    >({
      query: ({ id, agentIds }) => ({
        url: `/portal/api-integrations/${id}/agent-associations`,
        method: 'PUT',
        body: { agent_ids: agentIds },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<ApiIntegration>(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'ApiIntegration', id },
        { type: 'ApiIntegration', id: 'LIST' },
        'Agent',
      ],
    }),
  }),
});

export const {
  useGetApiIntegrationsQuery,
  useGetApiIntegrationQuery,
  useCreateApiIntegrationMutation,
  useUpdateApiIntegrationMutation,
  useDeleteApiIntegrationMutation,
  useVerifyApiIntegrationMutation,
  useUpdateApiAgentAssociationsMutation,
} = apiIntegrationsApi;
